import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, UserPlus, Search, Shield, UserX, UserCheck, ChevronDown, Loader2, Trash2, RefreshCw, AlertTriangle, Mail, X, Link2 } from 'lucide-react';
import { API, useAuth } from '../../App';
import UserDetailFlyout from './UserDetailFlyout';
// #1137 Task 4.2: Workspace-scope Impersonate button — reuses the same modal
// component as the platform-admin entry point in AdminPanel (Task 4.1) but
// posts to the workspace-scoped grant endpoint.
import ImpersonateConfirmModal from '../ImpersonateConfirmModal';
import { useImpersonation } from '../../contexts/ImpersonationContext';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const input = 'w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none min-h-[44px]';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00] transition-colors disabled:opacity-50';
const btnSecondary = 'px-3 py-1.5 border border-[#2A2A2A] text-[#A0A0A0] text-xs rounded hover:border-[#FF4D00] hover:text-[#F5F5F5] transition-colors';
const btnDanger = 'px-3 py-1.5 bg-[#A61E17] text-white text-xs rounded hover:bg-[#8B1912] transition-colors';

const ROLE_COLORS = {
  owner: '#FF4D00', admin: '#F2A900', user: '#00D4FF',
  account_super_admin: '#FF4D00',
};
const STATUS_COLORS = {
  accepted: '#22C55E', pending: '#F2A900', invited: '#00D4FF', disabled: '#A61E17',
};

export default function UsersSection({ workspaceId }) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  // #1137 Task 4.2: workspace-scope impersonation entry point.
  const { startImpersonation } = useImpersonation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteDept, setInviteDept] = useState('');
  const [invitePwMethod, setInvitePwMethod] = useState('user_set');
  const [inviteAdminPw, setInviteAdminPw] = useState('');
  const [departments, setDepartments] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  // #479: React-based confirm dialog (replaces window.confirm which can be blocked)
  const [confirmDelete, setConfirmDelete] = useState(null);
  // #488/#489: User detail flyout
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [actorRole, setActorRole] = useState('user');
  // #1137 Task 4.2: workspace impersonation state. We don't pre-filter
  // platform-admin targets client-side because the workspace `members` API
  // doesn't expose `users.role` (only `workspace_members.role`). The backend
  // refuses to mint a grant against a platform admin and the inline error
  // strip below surfaces that to the operator.
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  // #1137 final-review N1: Cancel-during-Confirm race guard. If the user
  // clicks Cancel while POST /api/workspaces/.../impersonate/grant is in
  // flight, the in-flight request will still resolve with a real grant_id
  // — without this ref the success branch would persist + navigate even
  // though the user said cancel. The ref flips to true on Cancel; the
  // success branch checks it, revokes the just-minted grant best-effort,
  // and bails. Reset on every modal open via the useEffect below.
  const impersonateCancelledRef = useRef(false);
  useEffect(() => {
    if (impersonateTarget) impersonateCancelledRef.current = false;
  }, [impersonateTarget]);

  const fetchMembers = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}`);
      const membersList = r.data.members || [];
      setMembers(membersList);
      // #489: Detect actor's role for permission scoping
      const me = await API.get('/api/auth/me');
      const myId = me.data?.id;
      const myMembership = membersList.find(m => m.id === myId);
      if (myMembership) setActorRole(myMembership.role || 'user');
    } catch { setMembers([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // #537: Fetch departments for invite-time assignment
  useEffect(() => {
    if (!workspaceId) return;
    API.get('/api/enterprise/departments', { params: { workspace_id: workspaceId } })
      .then(r => setDepartments(r.data?.departments || []))
      .catch(() => {});
  }, [workspaceId]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await API.post(`/api/workspaces/${workspaceId}/members`, {
        email: inviteEmail, name: inviteName || undefined, role: inviteRole,
        department_id: inviteDept || undefined, password_method: invitePwMethod,
        admin_password: invitePwMethod === 'admin_set' ? inviteAdminPw : undefined,
      });
      setInviteEmail(''); setInviteName(''); setInviteDept(''); setInvitePwMethod('user_set'); setInviteAdminPw(''); setShowInvite(false);
      fetchMembers();
    } catch (e) { alert(e.response?.data?.detail || 'Invite failed'); }
    setInviting(false);
  };

  const changeRole = async (userId, role) => {
    setActionLoading(userId);
    try {
      await API.put(`/api/workspaces/${workspaceId}/members/${userId}/role`, { role });
      fetchMembers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  const toggleDisable = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      await API.put(`/api/workspaces/${workspaceId}/members/${userId}/disable`, { disabled: currentStatus !== 'disabled' });
      fetchMembers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  // #482: Resend a pending workspace invite
  const resendInvite = async (inviteId) => {
    setActionLoading(`invite:${inviteId}`);
    try {
      const r = await API.post(`/api/workspaces/${workspaceId}/invites/${inviteId}/resend`);
      alert(`Invite resent to ${r.data?.email || 'user'}`);
    } catch (e) { alert(e.response?.data?.detail || 'Resend failed'); }
    setActionLoading(null);
  };

  // #536: Copy invite link for manual sharing
  const copyInviteLink = async (inviteId) => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/invites/${inviteId}/link`);
      await navigator.clipboard.writeText(r.data.url);
      alert(`Invite link copied for ${r.data.email}`);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to get invite link'); }
  };

  // #482: Revoke a pending workspace invite
  const revokeInvite = async (inviteId) => {
    setActionLoading(`invite:${inviteId}`);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/invites/${inviteId}`);
      setMembers(prev => prev.filter(m => m.id !== `invite:${inviteId}`));
    } catch (e) {
      alert(e.response?.data?.detail || 'Revoke failed');
      fetchMembers();
    }
    setActionLoading(null);
  };

  // #479: Two-step delete — set confirmDelete to show inline confirm, then execute
  const executeDelete = async (userId) => {
    setConfirmDelete(null);
    setActionLoading(userId);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/members/${userId}`);
      // Optimistically remove from local state for instant feedback
      setMembers(prev => prev.filter(m => m.id !== userId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove user');
      fetchMembers(); // Reload on error
    }
    setActionLoading(null);
  };

  const filtered = members.filter(m =>
    !search || (m.name || '').toLowerCase().includes(search.toLowerCase()) || (m.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="text-center py-10 text-[#A0A0A0]">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">
          <Users className="w-5 h-5 inline mr-2 text-[#FF4D00]" />
          Workspace Users ({members.length})
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => { setLoading(true); fetchMembers(); }} className={btnSecondary} title="Refresh user list">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setShowInvite(!showInvite)} className={btnPrimary}>
            <UserPlus className="w-4 h-4 inline mr-1" />Invite User
          </button>
        </div>
      </div>

      {showInvite && (
        <div className={card}>
          <h3 className="text-sm font-mono text-[#F2A900] mb-3">Invite a User</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@company.com" className={input} />
            </div>
            <div className="sm:col-span-2">
              <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Display name (optional)" className={input} />
            </div>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono min-h-[44px]">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            {departments.length > 0 && (
              <select value={inviteDept} onChange={e => setInviteDept(e.target.value)}
                className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono min-h-[44px]">
                <option value="">No department</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
          {/* #544: Password method */}
          <div className="mt-3 space-y-2">
            <label className="text-xs text-[#A0A0A0] font-mono block">Password</label>
            <div className="flex flex-col gap-1.5">
              {[
                { value: 'user_set', label: 'Let user set password on accept', desc: 'Simplest — user creates their own password' },
                { value: 'auto_generated', label: 'Auto-generate temporary password', desc: 'Shown to you — user must change on first login' },
                { value: 'admin_set', label: 'Set password manually', desc: 'You type a password — can optionally force change' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-2 cursor-pointer group">
                  <input type="radio" name="pw_method" value={opt.value} checked={invitePwMethod === opt.value}
                    onChange={() => setInvitePwMethod(opt.value)}
                    className="mt-1 accent-[#FF4D00]" />
                  <div>
                    <span className="text-sm text-[#F5F5F5]">{opt.label}</span>
                    <p className="text-[10px] text-[#666]">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            {invitePwMethod === 'admin_set' && (
              <input value={inviteAdminPw} onChange={e => setInviteAdminPw(e.target.value)}
                type="password" placeholder="Enter password for user" className={input + ' mt-2'} />
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()} className={btnPrimary}>
              {inviting ? 'Inviting...' : 'Send Invite'}
            </button>
            <button onClick={() => setShowInvite(false)} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
          className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded pl-9 pr-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none min-h-[44px]" />
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className={`${card} text-center`}>
            <Users className="w-8 h-8 mx-auto mb-2 text-[#A0A0A0] opacity-50" />
            <p className="text-sm text-[#A0A0A0]">{search ? 'No matching users' : 'No users in workspace'}</p>
          </div>
        ) : filtered.map(m => {
          const status = m.invite_status || 'accepted';
          const role = m.role || 'user';
          const isOwner = role === 'owner' || role === 'account_super_admin';
          const isInvited = status === 'invited';
          const isConfirming = confirmDelete === m.id;
          const canOpenFlyout = ['owner', 'admin', 'account_admin', 'account_super_admin'].includes(actorRole) && !isInvited;
          return (
            <div key={m.id}
              className={`${card} flex items-center justify-between ${isConfirming ? 'border-[#A61E17]' : ''} ${canOpenFlyout ? 'cursor-pointer hover:border-[#FF4D00]/50 transition-colors' : ''}`}
              onClick={() => canOpenFlyout && setSelectedUserId(m.id)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isInvited ? 'bg-[#00D4FF20] text-[#00D4FF]' : 'bg-[#2A2A2A] text-[#F5F1E8]'}`}>
                  {isInvited ? <Mail className="w-3.5 h-3.5" /> : (m.name || m.email || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#F5F1E8] font-mono truncate">{m.name || m.email}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-mono" style={{
                      color: ROLE_COLORS[role] || '#A0A0A0',
                      backgroundColor: `${ROLE_COLORS[role] || '#A0A0A0'}20`,
                    }}>{role.replace('account_', '').toUpperCase()}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-mono" style={{
                      color: STATUS_COLORS[status] || '#A0A0A0',
                      backgroundColor: `${STATUS_COLORS[status] || '#A0A0A0'}20`,
                    }}>{status.toUpperCase()}</span>
                  </div>
                  <span className="text-[10px] text-[#A0A0A0] font-mono">{m.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                {/* #1137 Task 4.2: workspace-scope Impersonate. Hidden when
                    target == self, when the row is a pending invite (no real
                    user account yet), and during other in-flight actions on
                    this row. We don't filter on platform-admin role here —
                    the members payload doesn't carry users.role, so the
                    backend refusal + the inline error strip is the gate. */}
                {!isInvited && m.id !== currentUserId && actionLoading !== m.id && !isConfirming && (
                  <button
                    data-testid={`impersonate-workspace-member-${m.id}`}
                    onClick={() => { setImpersonateError(null); setImpersonateTarget(m); }}
                    className="px-2 py-1 text-[10px] uppercase font-bold border border-[#A61E17] text-[#A61E17] hover:bg-[#A61E17] hover:text-[#F5F1E8] font-['Barlow_Condensed']"
                    title="Impersonate this user"
                  >
                    Impersonate
                  </button>
                )}
                {!isOwner && (
                  actionLoading === m.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-[#A0A0A0]" />
                  ) : isInvited ? (
                    /* #482/#536: Pending invite — resend, copy link, or revoke */
                    <>
                      <button onClick={() => resendInvite(m.invite_id)} className={btnSecondary} title="Resend invite email">
                        <Mail className="w-3 h-3" />
                      </button>
                      <button onClick={() => copyInviteLink(m.invite_id)} className={btnSecondary} title="Copy invite link">
                        <Link2 className="w-3 h-3" />
                      </button>
                      <button onClick={() => revokeInvite(m.invite_id)} className={btnDanger} title="Revoke invite">
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : isConfirming ? (
                    /* #479: Inline confirmation — no window.confirm needed */
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-[#A61E17] font-mono">Remove?</span>
                      <button onClick={() => executeDelete(m.id)}
                        className="px-2 py-1 bg-[#A61E17] text-white text-[10px] font-mono rounded hover:bg-[#8B1912]">
                        Yes
                      </button>
                      <button onClick={() => setConfirmDelete(null)}
                        className="px-2 py-1 border border-[#2A2A2A] text-[#A0A0A0] text-[10px] font-mono rounded hover:text-[#F5F5F5]">
                        No
                      </button>
                    </div>
                  ) : (
                    <>
                      <select value={role} onChange={e => changeRole(m.id, e.target.value)}
                        className="bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] font-mono text-[#A0A0A0] px-1.5 py-1 rounded">
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                      <button onClick={() => toggleDisable(m.id, status)}
                        className={btnSecondary} title={status === 'disabled' ? 'Enable' : 'Disable'}>
                        {status === 'disabled' ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                      </button>
                      <button onClick={() => setConfirmDelete(m.id)} className={btnDanger} title="Remove from workspace">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* #488/#489: User detail flyout */}
      {selectedUserId && (
        <UserDetailFlyout
          workspaceId={workspaceId}
          userId={selectedUserId}
          actorRole={actorRole}
          onClose={() => setSelectedUserId(null)}
          onRefresh={() => { fetchMembers(); }}
        />
      )}

      {/* #1137 Task 4.2: inline error strip for impersonation grant failures.
          Sits at the bottom of the section so it shows up next to the row
          actions the operator just clicked. */}
      {impersonateError && (
        <div
          className="px-3 py-2 bg-[#A61E17]/10 border border-[#A61E17]/30 text-xs text-[#A61E17] font-mono flex items-center justify-between gap-3"
          data-testid="impersonate-error"
        >
          <span className="truncate">{impersonateError}</span>
          <button
            onClick={() => setImpersonateError(null)}
            className="text-[#A61E17] hover:text-[#F5F1E8] shrink-0"
            aria-label="Dismiss error"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* #1137 Task 4.2: workspace-scope confirm modal. Sibling of the
          AdminPanel platform-scope flow but posts to the workspace grant
          endpoint and threads scope='workspace' so the modal copy reads
          "in their workspace scope". */}
      {impersonateTarget && (
        <ImpersonateConfirmModal
          target={impersonateTarget}
          scope="workspace"
          onCancel={() => {
            // N1: arm cancellation ref before closing so an in-flight
            // POST resolves into the revoke + bail branch.
            impersonateCancelledRef.current = true;
            setImpersonateTarget(null);
            setImpersonateError(null);
          }}
          onConfirm={async () => {
            if (impersonateLoading) return;
            setImpersonateLoading(true);
            setImpersonateError(null);
            try {
              const resp = await API.post(
                `/api/workspaces/${workspaceId}/impersonate/grant`,
                { target_user_id: impersonateTarget.id, reason: 'workspace admin debug' },
              );
              // N1: cancelled mid-flight → revoke the just-minted grant
              // best-effort and bail without persisting / navigating.
              if (impersonateCancelledRef.current) {
                if (resp?.data?.grant_id) {
                  API.post(
                    `/api/impersonate/grant/${resp.data.grant_id}/revoke`,
                    { reason: 'cancelled_in_flight' },
                  ).catch(() => {});
                }
                return;
              }
              setImpersonateTarget(null);
              // startImpersonation persists to sessionStorage and navigates
              // to '/' so role-gated routes re-evaluate cleanly.
              startImpersonation(resp.data);
            } catch (err) {
              if (impersonateCancelledRef.current) return;
              setImpersonateError(
                err?.response?.data?.detail || err?.message || 'Impersonation failed',
              );
              setImpersonateTarget(null);
            } finally {
              setImpersonateLoading(false);
            }
          }}
        />
      )}
    </div>
  );
}
